import { z } from 'zod';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from './client';

/**
 * Minimum-PII user display-name + avatar-tone lookup (T-0127).
 *
 *   GET /api/v1/users/lookup?ids=u1,u2,...
 *     → { items: [{ id, display_name, avatar_color_index }] }
 *
 * Accessible to any authenticated firm-root caller — non-firm-admins
 * need team-avatar rendering across the app (clients table, engagement
 * banner, settings tabs) and the alternative is UUID-prefix initials
 * everywhere. The endpoint emits ONLY the three display fields; no
 * email, no role, no status, no last_sign_in_at, no 2FA flag — those
 * remain firm_admin-only via the existing /users + /users/:id routes.
 *
 * `avatar_color_index` is a djb2 hash over `id + name` modulo 6, +1
 * (same algorithm as `apps/web/src/components/clients/team-avatars.tsx
 * → avatarTone`). Server and client agree on the tone slot without the
 * client re-deriving from raw fields.
 *
 * Backend constraints (enforced):
 *   - 100-id ceiling per request; dedup happens server-side.
 *   - Unknown ids silently omitted.
 *   - Deactivated users ARE returned (historical attribution).
 *   - 400 if any id is not a valid uuid.
 */

export const UserLookupItemSchema = z.object({
  id: z.string().uuid(),
  display_name: z.string(),
  avatar_color_index: z.number().int().min(1).max(6),
});
export type UserLookupItem = z.infer<typeof UserLookupItemSchema>;

/**
 * The API returns a BARE ARRAY (`[{ id, display_name, avatar_color_index }]`
 * — see apps/api routes/users.ts + users.integration.test.ts). This client
 * originally expected `{ items: [...] }`, so every lookup failed schema
 * validation and callers silently fell back to raw ids. Accept the real
 * shape (and the wrapped one, defensively) and normalize to `{ items }`
 * so existing consumers keep reading `.items`.
 */
export const UserLookupResponseSchema = z.union([
  z.array(UserLookupItemSchema),
  z.object({ items: z.array(UserLookupItemSchema) }),
]);
export type UserLookupResponse = { items: UserLookupItem[] };

const MAX_IDS_PER_REQUEST = 100;

/** De-dup + cap input ids so we never violate the backend's 100-id ceiling. */
function normalizeIds(ids: ReadonlyArray<string>): string[] {
  return Array.from(new Set(ids)).slice(0, MAX_IDS_PER_REQUEST);
}

export function lookupUsers(ids: ReadonlyArray<string>): Promise<UserLookupResponse> {
  const normalized = normalizeIds(ids);
  if (normalized.length === 0) {
    return Promise.resolve({ items: [] });
  }
  return apiClient('/api/v1/users/lookup', UserLookupResponseSchema, {
    query: { ids: normalized.join(',') },
  }).then((raw) => (Array.isArray(raw) ? { items: raw } : raw));
}

export function useUsersLookupQuery(ids: ReadonlyArray<string>) {
  const normalized = normalizeIds(ids);
  return useQuery({
    // Sort for cache stability so unrelated order changes don't invalidate.
    queryKey: ['users', 'lookup', [...normalized].sort().join(',')] as const,
    queryFn: () => lookupUsers(normalized),
    enabled: normalized.length > 0,
    staleTime: 5 * 60_000,
  });
}
